import type { ImageDialogProps } from '../ImageDialog/index.ts';
import clsx from 'clsx';
import { PiCaretLeftBold, PiCaretRightBold } from 'react-icons/pi';
import { useDialog } from '@_/hooks';
import {
  Carousel,
  CarouselDots,
  CarouselNextButton,
  CarouselPrevButton,
  CarouselSlide,
  CarouselSlides,
  Image
} from '@_/ui';
import { ImageDialog } from '../ImageDialog/index.ts';

interface ImageCarouselProps extends Pick<ImageDialogProps, 'imageUrls' | 'initialIndex'> {
  showDots?: boolean;
}

export function ImageCarousel({ imageUrls, initialIndex, showDots = false }: ImageCarouselProps) {
  const imageDialog = useDialog({
    component: ImageDialog,
    openFunctionArgsToProps: (initialIndex: number) => ({ initialIndex }),
    props: { imageUrls }
  });

  const navButtonStyles = clsx(
    'hidden sm:flex',
    'z-1 size-7 items-center justify-center rounded-full border-black/10 drop-shadow-md transition-[opacity,scale,background-color] duration-150 ease-out motion-reduce:transition-none',
    'bg-white/90 opacity-0 group-hover:opacity-100 hover:scale-105 hover:bg-white motion-reduce:hover:scale-100'
  );

  return (
    <>
      {imageDialog.element}

      <Carousel initialIndex={initialIndex}>
        <div className="group relative size-full">
          <div className="absolute inset-0 flex flex-col justify-center">
            <div className="mx-3 flex justify-between">
              <CarouselPrevButton className={clsx(navButtonStyles, 'left-3')}>
                <PiCaretLeftBold className="size-4" />
              </CarouselPrevButton>

              <CarouselNextButton className={clsx(navButtonStyles, 'right-3')}>
                <PiCaretRightBold className="size-4" />
              </CarouselNextButton>
            </div>
          </div>

          <CarouselSlides>
            {imageUrls.map((url, index) => (
              <CarouselSlide
                key={url}
                className="size-full cursor-pointer"
                onClick={() => imageDialog.open(index)}
                render={<Image fit="cover" src={url} />}
              />
            ))}
          </CarouselSlides>

          {showDots && (
            <div className="absolute inset-x-0 bottom-[5%] z-10">
              <CarouselDots />
            </div>
          )}
        </div>
      </Carousel>
    </>
  );
}
